import React, { useState, useEffect } from 'react';
import {
  FolderKanban,
  Plus,
  Search,
  Building2,
  Calendar,
  DollarSign,
  TrendingUp,
  ArrowRight,
  Filter,
} from 'lucide-react';
import { ProjectItem } from '../types/index.ts';

interface ProjectsPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const ProjectsPage: React.FC<ProjectsPageProps> = ({ onNavigate }) => {
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showAddModal, setShowAddModal] = useState(false);

  const [form, setForm] = useState({
    projectName: '',
    branchId: '',
    ticketId: '',
    poNumber: '',
    poDate: '',
    poAmount: '',
    contractValue: '',
    budget: '',
    projectManager: 'Engr. Tariq Mehmood',
    siteEngineer: 'Engr. Bilal Hashmi',
    startDate: new Date().toISOString().split('T')[0],
    targetCompletionDate: '',
  });

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const [pRes, bRes, tRes] = await Promise.all([
        fetch('/api/projects'),
        fetch('/api/branches'),
        fetch('/api/tickets'),
      ]);
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) setProjects(pData);
      }
      if (bRes.ok) {
        const bData = await bRes.json();
        if (Array.isArray(bData)) {
          setBranches(bData);
          if (bData.length > 0) {
            setForm((prev) => ({ ...prev, branchId: String(bData[0].id) }));
          }
        }
      }
      if (tRes.ok) {
        const tData = await tRes.json();
        if (Array.isArray(tData)) setTickets(tData);
      }
    } catch (err) {
      console.warn('Projects fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowAddModal(false);
        fetchProjects();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  const filtered = (Array.isArray(projects) ? projects : []).filter((p) => {
    const matchesSearch =
      (p.projectNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.projectName || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.branchName && p.branchName.toLowerCase().includes(search.toLowerCase())) ||
      (p.poNumber && p.poNumber.toLowerCase().includes(search.toLowerCase()));

    const matchesStatus = statusFilter === 'ALL' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Projects Center
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900">
              {projects.length} Active &amp; Completed
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Central repository for UBL renovation, civil, electrical, and finishing projects.
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4 text-amber-400" />
          <span>New Project</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200">
        <div className="flex items-center gap-2 w-full sm:max-w-md">
          <Search className="w-4 h-4 text-slate-400 ml-1" />
          <input
            type="text"
            className="w-full text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden"
            placeholder="Search project number (e.g. NB-PRJ-001), branch, PO number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <span className="text-xs text-slate-500 font-medium">Status:</span>
          <select
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-slate-50 text-slate-700 focus:outline-hidden"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Statuses</option>
            <option value="PLANNING">Planning</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="INVOICED">Invoiced</option>
          </select>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Project ID &amp; Name</th>
                <th className="py-3 px-4">Target Branch</th>
                <th className="py-3 px-4">Contract / PO Value</th>
                <th className="py-3 px-4">Actual Cost</th>
                <th className="py-3 px-4">Gross Profit</th>
                <th className="py-3 px-4">Engineers</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => {
                const profitVal = Number(p.profit || 0);
                return (
                  <tr
                    key={p.id}
                    onClick={() => onNavigate('project-detail', String(p.id))}
                    className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="font-bold text-blue-900">{p.projectNumber}</div>
                      <div className="font-semibold text-slate-800 line-clamp-1">{p.projectName}</div>
                      {p.poNumber && (
                        <div className="text-[10px] text-slate-400">PO: {p.poNumber}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-800">{p.branchName}</div>
                      <div className="text-[11px] text-slate-500">{p.branchCode} • {p.city}</div>
                    </td>
                    <td className="py-3 px-4 font-bold text-slate-900">
                      {formatPKR(p.contractValue)}
                    </td>
                    <td className="py-3 px-4 font-medium text-slate-700">
                      {formatPKR(p.actualCost)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-bold ${profitVal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {formatPKR(profitVal)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-slate-800 font-medium">PM: {p.projectManager}</div>
                      <div className="text-[11px] text-slate-500">Site: {p.siteEngineer}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          p.status === 'COMPLETED'
                            ? 'bg-emerald-100 text-emerald-800'
                            : p.status === 'IN_PROGRESS'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {p.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onNavigate('project-detail', String(p.id));
                        }}
                        className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-2.5 py-1 rounded transition-colors"
                      >
                        Open Details →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Project Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Create New Project Job</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Project Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. UBL Tariq Road Branch Renovation & Modernization"
                  className="w-full border border-slate-200 rounded-lg p-2 focus:ring-1 focus:ring-blue-500"
                  value={form.projectName}
                  onChange={(e) => setForm({ ...form, projectName: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Target Branch *</label>
                  <select
                    required
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.branchId}
                    onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                  >
                    {(Array.isArray(branches) ? branches : []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branchCode} - {b.branchName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Link Maintenance Ticket</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.ticketId}
                    onChange={(e) => setForm({ ...form, ticketId: e.target.value })}
                  >
                    <option value="">No linked ticket</option>
                    {(Array.isArray(tickets) ? tickets : []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.ticketNumber} - {t.subject}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Client PO Number</label>
                  <input
                    type="text"
                    placeholder="e.g. UBL-PO-2026-9021"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.poNumber}
                    onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Contract / PO Value (Rs.)</label>
                  <input
                    type="number"
                    placeholder="e.g. 1500000"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.contractValue}
                    onChange={(e) => setForm({ ...form, contractValue: e.target.value, poAmount: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Project Manager</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.projectManager}
                    onChange={(e) => setForm({ ...form, projectManager: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Site Engineer</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.siteEngineer}
                    onChange={(e) => setForm({ ...form, siteEngineer: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Target Completion Date</label>
                  <input
                    type="date"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.targetCompletionDate}
                    onChange={(e) => setForm({ ...form, targetCompletionDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  Initialize Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
